import { PayloadAction } from '@reduxjs/toolkit';
import {
  AuthSelectors,
  ConfigSelectors,
  EErrorLevel,
  LanguageActions,
  LanguageSelectors,
  LanguageService,
  logger,
  StatsActions,
  StatsSelectors,
} from '@web_opxp/features';
import { all, call, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import * as UnityEngine from 'web_unity_engine';
import { GAMEPLAY_BACKGROUND, GAMEPLAY_FULLSCREEN, GAMEPLAY_SOUND } from '../base/constants';
import ENV from '../base/env';
import { FULLSCREEN_CLASS } from '../base/fullscreenHelper';
import { TApiFetchResponse } from '../base/types';
import UnityCacheService from './configCache';
import ConfigService from './configService';
import { CODE_URL, DATA_URL, FRAMEWORK_URL } from './constants';
import * as Firebase from './firebaseActions';
import * as Selectors from './selectors';
import { UnityActions } from './slice';
import {
  IExerciseBalancingConfig,
  IInitUnityPayload,
  ILoadBalancingPayload,
  IPrepareExercisePayload,
  IStartExercisePayload,
  IStatState,
  ITutorialState,
  UnityError,
} from './types';
import UnityUtils from './utils';

export function* initUnityEngineSaga(action: PayloadAction<IInitUnityPayload>) {
  const { exerciseType, basePath } = action.payload;
  // check if already started
  const isStarted = yield select(Selectors.isUnityStarted);
  if (isStarted) {
    return;
  }

  yield put(Firebase.unityEngineEventAction('', exerciseType, Firebase.EEngineTrackingEvent.startLoading));
  const unityPath = basePath === undefined || basePath === '' ? ENV.UnityPath : basePath;
  // initialize config service with base path
  yield call(ConfigService.setBasePath, unityPath);
  // initialize cache service with base path
  yield call(UnityCacheService.init, unityPath);
  // initialize engine - must be done only once
  yield call(initUnityEngine, unityPath);
  // start unity - can be repeated when language has changed
  yield call(startUnityEngine);
  yield put(Firebase.unityEngineEventAction('', exerciseType, Firebase.EEngineTrackingEvent.finishLoading));
  yield put(UnityActions.unityStarted());
}

// this part is independent of the current language
// and can be done only once in a lifetime of the app
export function* initUnityEngine(basePath: string) {
  const unitySupport = yield call(UnityUtils.unitySupportStatus);
  if (unitySupport !== UnityUtils.SupportStatusEnum.TECH_READY) {
    let loading = yield select(LanguageSelectors.isLoading);
    while (loading) {
      yield take(LanguageActions.updateLanguage);
      loading = yield select(LanguageSelectors.isLoading);
    }
    const t = LanguageService.getTFunction();
    throw new UnityError(t('common:Errors.Unity.WebGL'));
  }
  yield put(
    UnityEngine.Actions.unityInit(basePath, ENV.UnityCanvasId, FULLSCREEN_CLASS, DATA_URL, CODE_URL, FRAMEWORK_URL)
  );
  yield take(UnityEngine.ActionTypes.UNITY_APP_READY);
}

// this part is language dependant and needs to be rerun each time language change
export function* startUnityEngine() {
  const basePath: string = yield call(ConfigService.getBasePath);
  const currentAppLanguage = yield select(LanguageSelectors.getLanguage);
  // preload translations to show translated errors
  yield call(loadUnityTranslationsSaga, currentAppLanguage);

  const appConfig: UnityEngine.UnityEngineAppConfig = yield call(loadUnityAppConfigSaga);
  appConfig.basePath = basePath;

  const isModerator: boolean = yield select(AuthSelectors.isModerator);
  const enableLogging = isModerator || ENV.UnityDebug ? 1 : 0;
  appConfig.enableLogging = enableLogging;
  appConfig.language = currentAppLanguage;
  logger(`startUnity currentAppLanguage=${currentAppLanguage}`);
  // apply general localization
  const t: ReturnType<typeof LanguageService.getTFunction> = yield call(LanguageService.getTFunction);
  appConfig.localization = t('unity.general:unity', {
    returnObjects: true,
  });
  yield put(UnityEngine.Actions.unityAppInit(appConfig));
  yield take(UnityEngine.ActionTypes.UNITY_ENGINE_READY);
}

export function* loadUnityAppConfigSaga() {
  // check config saved in cache
  const cachedResponse: UnityEngine.UnityEngineAppConfig | undefined = yield call(UnityCacheService.get, 'appConfig');
  if (cachedResponse) {
    return cachedResponse;
  }
  // fetch config if not in cache or expired
  try {
    const appConfigResponse: TApiFetchResponse<typeof ConfigService.getUnityAppConfig> = yield call(
      ConfigService.getUnityAppConfig
    );
    // saved fetched config to cache
    yield call(UnityCacheService.set, 'appConfig', appConfigResponse.data);
    return appConfigResponse.data;
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    const t = LanguageService.getTFunction();
    throw new UnityError(t('common:Errors.Unity.LoadUnityAppConfigFailed'));
  }
}

export function* loadUnityTranslationsSaga(language: string) {
  try {
    const errorlocaleLoaded = LanguageService.hasResourceBundle(language, 'common');
    const localeLoaded = LanguageService.hasResourceBundle(language, 'unity.general');
    if (!localeLoaded || !errorlocaleLoaded) {
      // load unity general namespace and common namespace with error locales
      yield call(LanguageService.loadNamespaces, ['unity.general', 'common']);
    }
  } catch {
    throw new UnityError('Localization loading failed');
  }
}

export function* prepareExerciseSaga(action: PayloadAction<IPrepareExercisePayload>) {
  const { exerciseId, workoutId } = action.payload;
  const exerciseType = yield select(Selectors.getCurrentExerciseType);
  yield put(Firebase.unityEngineEventAction(exerciseId, exerciseType, Firebase.EEngineTrackingEvent.startLoadingEx));
  // fetch workout config and balancing from S3
  const exerciseJsonFile = yield select(ConfigSelectors.getExerciseJsonFile, workoutId);
  const workoutSettings = yield call(loadWorkoutSettingsSaga, exerciseId, exerciseJsonFile);
  // get translations
  const exerciseLocale = yield call(loadExerciseTranslationsSaga, exerciseId, exerciseType);
  // overload existing localization with loaded one
  const locale = {
    ...workoutSettings.localization,
    ...exerciseLocale,
  };
  workoutSettings.localization = locale;
  // get user settings
  const soundStat = yield select(StatsSelectors.getStatIntValueById, GAMEPLAY_SOUND);
  const backgroundStat = yield select(StatsSelectors.getStatIntValueById, GAMEPLAY_BACKGROUND);
  const userId = yield select(AuthSelectors.getUserId);
  const token = yield select(AuthSelectors.getToken);
  const gameplaySettings: UnityEngine.IGameplaySettings = {
    sound: soundStat !== undefined ? soundStat : 1,
    background: backgroundStat !== undefined ? backgroundStat : 1,
    uid: userId,
    balancingName: exerciseJsonFile,
    token: token,
  };
  workoutSettings.userSettings = gameplaySettings;
  yield put(UnityEngine.Actions.unityExerciseInit(workoutSettings));
  yield take(UnityEngine.ActionTypes.UNITY_EXERCISE_READY);
  yield put(Firebase.unityEngineEventAction(exerciseId, exerciseType, Firebase.EEngineTrackingEvent.finishLoadingEx));
  yield put(UnityActions.exerciseReady());
}

export function* loadWorkoutSettingsSaga(exerciseId: string, exerciseJsonFile: string) {
  const settings: UnityEngine.UnityEngineExerciseSettings = yield call(loadExerciseSettingsSaga, exerciseId);
  yield put(UnityActions.loadBalancing(exerciseId, exerciseJsonFile));
  const balancingLoaded: PayloadAction<IExerciseBalancingConfig> = yield take(UnityActions.balancing);
  if (balancingLoaded.payload) {
    // override balancing if config loaded
    settings.override = balancingLoaded.payload;
  }
  return settings;
}

export function* loadWorkoutBalancing(exerciseId: string, exerciseJsonFile: string) {
  // check for balancing in cache
  const cacheKey = `balancing-${exerciseId}-${exerciseJsonFile}`;
  const cachedResponse: IExerciseBalancingConfig | undefined = yield call(UnityCacheService.get, cacheKey);
  if (cachedResponse) {
    return cachedResponse;
  }
  try {
    const workoutBalancingResponse: TApiFetchResponse<typeof ConfigService.getWorkoutBalancing> = yield call(
      ConfigService.getWorkoutBalancing,
      exerciseId,
      exerciseJsonFile
    );
    // save fetched balancing to cache
    yield call(UnityCacheService.set, cacheKey, workoutBalancingResponse.data);
    return workoutBalancingResponse.data;
  } catch {
    // custom balancing is not available - fallback to builtin
    return undefined;
  }
}

export function* loadExerciseBalancingSaga(action: PayloadAction<ILoadBalancingPayload>) {
  const { exerciseId, exerciseJsonFile } = action.payload;
  const balancing: IExerciseBalancingConfig | undefined = yield call(
    loadWorkoutBalancing,
    exerciseId,
    exerciseJsonFile
  );
  yield put(UnityActions.balancing(balancing));
}

export function* loadExerciseSettingsSaga(exerciseId: string) {
  const exerciseType = yield select(Selectors.getCurrentExerciseType);
  // check for settings in cache
  const cacheKey = `settings-${exerciseId}-${exerciseType}`;
  const cachedResponse: UnityEngine.UnityEngineExerciseSettings | undefined = yield call(
    UnityCacheService.get,
    cacheKey
  );
  if (cachedResponse) {
    return cachedResponse;
  }
  // fetch settings from remote if not found in cache or expired
  try {
    const isAssessment = exerciseType === 'assessment';
    const workoutSettingsResponse: TApiFetchResponse<typeof ConfigService.getExerciseSettings> = yield call(
      ConfigService.getExerciseSettings,
      exerciseId,
      isAssessment
    );
    // save fetched settings to cache
    yield call(UnityCacheService.set, cacheKey, workoutSettingsResponse.data);
    return workoutSettingsResponse.data;
  } catch {
    const t = LanguageService.getTFunction();
    throw new UnityError(t('common:Errors.Unity.LoadWorkoutSettingsFailed'));
  }
}

export function* loadExerciseTranslationsSaga(exerciseId: string, exerciseType: 'training' | 'assessment') {
  try {
    const language = yield select(LanguageSelectors.getLanguage);
    const exerciseName =
      exerciseId === 'Symbolism' && exerciseType === 'assessment' ? 'SymbolismAssessment' : exerciseId;
    const exerciseNamespace = `unity.${exerciseName}`;
    const localeLoaded = LanguageService.hasResourceBundle(language, exerciseNamespace);
    if (!localeLoaded) {
      // load general namespace
      yield call(LanguageService.loadNamespaces, exerciseNamespace);
    }
    const exerciseLocale = LanguageService.getResourceBundle(language, exerciseNamespace);
    return exerciseLocale;
  } catch {
    const t = LanguageService.getTFunction();
    throw new UnityError(t('common:Errors.Unity.LoadExerciseTranslationsFailed'));
  }
}

export function* getUserSettingsFromStats(categoryGroupId?: number) {
  if (!categoryGroupId) {
    return {};
  }
  const tutorialStatId = `0-${categoryGroupId}-9`;
  const tutorialStatValue: string | undefined = yield select(StatsSelectors.getStatStringValueById, tutorialStatId);
  const tutorialStatLastEdit: number | undefined = yield select(StatsSelectors.getStatLastEdit, tutorialStatId);
  const tutorialState: ITutorialState | undefined = yield call(
    UnityUtils.parseTutorialStateStat,
    tutorialStatValue,
    tutorialStatLastEdit
  );
  const readStateStatId = `0-${categoryGroupId}-10`;
  const readStateStatValue: string | undefined = yield select(StatsSelectors.getStatStringValueById, readStateStatId);
  const readState: IStatState | undefined = yield call(UnityUtils.parseReadStateStat, readStateStatValue);
  const solutionConfirmStatId = `0-${categoryGroupId}-11`;
  const solutionConfirmStatValue: string | undefined = yield select(
    StatsSelectors.getStatStringValueById,
    solutionConfirmStatId
  );
  const solutionConfirm: IStatState | undefined = yield call(
    UnityUtils.parseSolutionConfirmStat,
    solutionConfirmStatValue
  );
  return {
    tutorial: tutorialState,
    messagesReadState: readState,
    showSolutionWithConfirmState: solutionConfirm,
  };
}

export function* startExerciseSaga(action: PayloadAction<IStartExercisePayload>) {
  const { difficulty, duration, goal, exerciseRun } = action.payload;
  const exerciseId = yield select(Selectors.getCurrentExerciseId);
  const exerciseType = yield select(Selectors.getCurrentExerciseType);
  yield put(Firebase.unityEngineEventAction(exerciseId, exerciseType, Firebase.EEngineTrackingEvent.start));
  const categoryGroupId: number | undefined = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
  const userSettings = yield call(getUserSettingsFromStats, categoryGroupId);
  const options: UnityEngine.ExerciseWorkoutOptions = {
    difficulty,
    goal,
    mode: UnityEngine.WorkoutMode.Workout,
    options: JSON.stringify({
      preventInputPause: true,
      timeout: duration,
    }),
    ...userSettings,
  };
  // set exerciseRun only if it is > 0
  if (exerciseRun) {
    options.exerciseRun = exerciseRun;
  }
  const assessmentRun = yield select(Selectors.getAssessmentRun);
  if (assessmentRun) {
    options.assessmentRun = assessmentRun;
  }
  yield put(UnityEngine.Actions.unityExerciseStart(options));
}

export function* startLearningModeSaga(action: PayloadAction<number>) {
  const difficulty = action.payload;
  const exerciseId = yield select(Selectors.getCurrentExerciseId);
  yield put(Firebase.learningModeEventAction(exerciseId, Firebase.ELearningModeTrackingEvent.Start));
  const categoryGroupId: number | undefined = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
  const userSettings = yield call(getUserSettingsFromStats, categoryGroupId);
  const options: UnityEngine.ExerciseWorkoutOptions = {
    difficulty,
    goal: 0,
    mode: UnityEngine.WorkoutMode.Learning,
    options: JSON.stringify({
      preventInputPause: true,
      timeout: 0,
    }),
    ...userSettings,
  };
  yield put(UnityEngine.Actions.unityExerciseStart(options));
}

export function* unityExerciseCompletedSaga({ payload }: UnityEngine.ActionTypes.IUnityExerciseCompleteAction) {
  const mode: ReturnType<typeof Selectors.getCurrentRunningMode> = yield select(Selectors.getCurrentRunningMode);
  if (!mode) {
    logger('Exercise mode not set when exercise is completed!', EErrorLevel.Warning);
    return;
  }
  const exerciseId: string = yield select(Selectors.getCurrentExerciseId);
  const exerciseType: ReturnType<typeof Selectors.getCurrentExerciseType> = yield select(
    Selectors.getCurrentExerciseType
  );
  const unityWorkoutResult: UnityEngine.WorkoutUnityResult = JSON.parse(payload);
  yield put(UnityActions.exerciseComplete(unityWorkoutResult));
  yield put(StatsActions.saveStatsQueue());
  if (mode === 'exercise') {
    yield put(Firebase.unityEngineEventAction(exerciseId, exerciseType, Firebase.EEngineTrackingEvent.complete));
    return;
  }
  yield put(Firebase.learningModeEventAction(exerciseId, Firebase.ELearningModeTrackingEvent.End));
}

export function* trackEventSaga({ payload }: UnityEngine.ActionTypes.IUnityTrackEventAction) {
  const exerciseId = yield select(Selectors.getCurrentExerciseId);
  try {
    if (payload) {
      const eventsObject = JSON.parse(payload);
      for (const event in eventsObject) {
        if (Object.hasOwn(eventsObject, event)) {
          yield put(Firebase.firebaseUnityTrackingAction(exerciseId, event, eventsObject[event]));
        }
      }
    }
  } catch {
    console.warn('tracking event format incorrect:', payload);
  }
}

export function* updateUnityGameplaySoundSettingsSaga({
  payload,
}: UnityEngine.ActionTypes.IUnityGameplaySoundSettingsUpdatedAction) {
  const isWorkoutRunning = yield select(Selectors.isRunning);
  if (!isWorkoutRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_SOUND, payload));
}

export function* updateUnityGameplayBackgroundSettingsSaga({
  payload,
}: UnityEngine.ActionTypes.IUnityGameplayBackgroundSettingsUpdatedAction) {
  const isWorkoutRunning = yield select(Selectors.isRunning);
  if (!isWorkoutRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_BACKGROUND, payload));
}

export function* updateUnityGameplayFullscreenSettingsSaga({
  payload,
}: UnityEngine.ActionTypes.IUnityGameplayFullscreenSettingsUpdatedAction) {
  const isWorkoutRunning = yield select(Selectors.isRunning);
  if (!isWorkoutRunning) {
    return;
  }
  yield put(StatsActions.updateNumericStat(GAMEPLAY_FULLSCREEN, payload));
}

export function* updateUserStatsSaga({ statId, payload }: UnityEngine.ActionTypes.IUnityStatsUpdateAction) {
  const isWorkoutRunning = yield select(Selectors.isRunning);
  if (!isWorkoutRunning) {
    return;
  }
  try {
    // parse statId for placeholders
    let parsedStatId = statId;
    if (UnityUtils.testForCategoryGroupIdPlaceholder(parsedStatId)) {
      const exerciseId = yield select(Selectors.getCurrentExerciseId);
      const categoryGroupId: number | undefined = yield select(ConfigSelectors.getCategoryGroupId, exerciseId);
      parsedStatId = UnityUtils.replaceCategoryGroupIdPlaceholder(parsedStatId, categoryGroupId);
    }
    yield put(StatsActions.updateGenericStat(parsedStatId, payload));
  } catch (error) {
    yield call(console.log, error);
  }
}

export function* restartUnityEngineSaga() {
  const unityStarted = yield select(Selectors.isUnityStarted);
  if (unityStarted) {
    // this will only stop processes after initialization
    // which is OK for our case - we will restart everything later
    yield put(UnityEngine.Actions.unityStop());
  } else {
    // wait until initialization flow finished
    yield take(UnityActions.unityStarted);
  }
  // restart unity with (probably) new language
  yield call(startUnityEngine);
  yield put(UnityActions.unityRestarted());
}

export default function* unityWatcher() {
  yield all([
    takeLeading(UnityActions.initUnity, initUnityEngineSaga),
    takeLatest(UnityActions.unityRestart, restartUnityEngineSaga),
    takeEvery(UnityActions.prepareExercise, prepareExerciseSaga),
    takeEvery(UnityActions.startExercise, startExerciseSaga),
    takeEvery(UnityActions.startLearning, startLearningModeSaga),
    takeEvery(UnityActions.loadBalancing, loadExerciseBalancingSaga),
    takeEvery(UnityEngine.ActionTypes.UNITY_TRACK_EVENT, trackEventSaga),
    takeEvery(UnityEngine.ActionTypes.UNITY_GAMEPLAY_SOUND_SETTINGS_UPDATED, updateUnityGameplaySoundSettingsSaga),
    takeEvery(
      UnityEngine.ActionTypes.UNITY_GAMEPLAY_BACKGROUND_SETTINGS_UPDATED,
      updateUnityGameplayBackgroundSettingsSaga
    ),
    takeEvery(
      UnityEngine.ActionTypes.UNITY_GAMEPLAY_FULLSCREEN_SETTINGS_UPDATED,
      updateUnityGameplayFullscreenSettingsSaga
    ),
    takeEvery(UnityEngine.ActionTypes.UNITY_STATS_UPDATE, updateUserStatsSaga),
    takeEvery(UnityEngine.ActionTypes.UNITY_EXERCISE_COMPLETE, unityExerciseCompletedSaga),
  ]);
}
